import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CheckSquare,
  Clock,
  Inbox,
  Circle,
  AlertTriangle,
  Hourglass,
  MessageSquare,
  User,
  X,
} from 'lucide-react';
import { apiGet } from '../lib/api';

interface DashboardStats {
  openTasks: number;
  dueThisWeek: number;
  meetingsToday: number;
  inboxItems: number;
}

interface DashboardTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  person_id?: string;
  person_name?: string;
}

interface DashboardMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  series_name: string;
  series_id: string;
}

interface DashboardCapture {
  id: string;
  text: string;
  status: string;
  created_at: string;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={12} className="text-text-muted" />,
  in_progress: <Clock size={12} className="text-accent" />,
  blocked: <AlertTriangle size={12} className="text-danger" />,
  done: <CheckSquare size={12} className="text-success" />,
  cancelled: <X size={12} className="text-text-muted" />,
};

const PRIORITY_DOT_COLORS: Record<string, string> = {
  urgent: 'bg-danger',
  high: 'bg-warning',
  medium: 'bg-text-secondary',
  low: 'bg-text-muted',
};

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [overdueTasks, setOverdueTasks] = useState<DashboardTask[]>([]);
  const [dueThisWeek, setDueThisWeek] = useState<DashboardTask[]>([]);
  const [meetingsToday, setMeetingsToday] = useState<DashboardMeeting[]>([]);
  const [recentCaptures, setRecentCaptures] = useState<DashboardCapture[]>([]);
  const [waitingOn, setWaitingOn] = useState<DashboardTask[]>([]);
  const [followUps, setFollowUps] = useState<DashboardTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [s, o, d, m, c, w, f] = await Promise.all([
          apiGet<DashboardStats>('/dashboard/stats'),
          apiGet<DashboardTask[]>('/dashboard/overdue'),
          apiGet<DashboardTask[]>('/dashboard/due-this-week'),
          apiGet<DashboardMeeting[]>('/dashboard/meetings-today'),
          apiGet<DashboardCapture[]>('/dashboard/recent-captures'),
          apiGet<DashboardTask[]>('/dashboard/waiting-on'),
          apiGet<DashboardTask[]>('/dashboard/follow-ups'),
        ]);
        setStats(s);
        setOverdueTasks(o);
        setDueThisWeek(d);
        setMeetingsToday(m);
        setRecentCaptures(c);
        setWaitingOn(w);
        setFollowUps(f);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  // Group waiting-on tasks by person
  const waitingByPerson = new Map<string, { name: string; tasks: DashboardTask[] }>();
  for (const t of waitingOn) {
    const key = t.person_id || 'unknown';
    if (!waitingByPerson.has(key)) {
      waitingByPerson.set(key, { name: t.person_name || 'Unknown', tasks: [] });
    }
    waitingByPerson.get(key)!.tasks.push(t);
  }

  // Group follow-ups by person
  const followUpsByPerson = new Map<string, { name: string; tasks: DashboardTask[] }>();
  for (const t of followUps) {
    const key = t.person_id || 'unknown';
    if (!followUpsByPerson.has(key)) {
      followUpsByPerson.set(key, { name: t.person_name || 'Unknown', tasks: [] });
    }
    followUpsByPerson.get(key)!.tasks.push(t);
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Dashboard</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CheckSquare} label="Open Tasks" value={stats?.openTasks ?? 0} />
        <StatCard icon={Clock} label="Due This Week" value={stats?.dueThisWeek ?? 0} />
        <StatCard icon={Calendar} label="Meetings Today" value={stats?.meetingsToday ?? 0} />
        <StatCard icon={Inbox} label="Inbox Items" value={stats?.inboxItems ?? 0} />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Meetings */}
        <Section title="Today's Meetings" icon={<Calendar size={14} />}>
          {meetingsToday.length === 0 ? (
            <EmptyState message="No meetings scheduled for today." />
          ) : (
            <div className="space-y-1">
              {meetingsToday.map(m => (
                <Link
                  key={m.id}
                  to={`/meetings/${m.series_id}/${m.id}`}
                  className="flex items-center gap-2 px-2 py-2 rounded hover:bg-bg-hover transition-colors group"
                >
                  <Calendar size={14} className="text-warning flex-shrink-0" />
                  <span className="text-sm text-text-primary group-hover:text-accent transition-colors truncate flex-1">
                    {m.title || m.series_name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Overdue Tasks */}
        <Section title="Overdue Tasks" icon={<AlertTriangle size={14} className="text-danger" />}>
          {overdueTasks.length === 0 ? (
            <EmptyState message="No overdue tasks. You're all caught up." />
          ) : (
            <div className="space-y-0.5">
              {overdueTasks.map(t => <TaskRow key={t.id} task={t} showDueDate />)}
            </div>
          )}
        </Section>

        {/* Tasks Due This Week */}
        <Section title="Due This Week" icon={<Clock size={14} />}>
          {dueThisWeek.length === 0 ? (
            <EmptyState message="No tasks due this week." />
          ) : (
            <div className="space-y-0.5">
              {dueThisWeek.map(t => <TaskRow key={t.id} task={t} showDueDate />)}
            </div>
          )}
        </Section>

        {/* Recent Quick Captures */}
        <Section title="Inbox" icon={<Inbox size={14} />}>
          {recentCaptures.length === 0 ? (
            <EmptyState message="Nothing in your inbox." />
          ) : (
            <div className="space-y-1">
              {recentCaptures.map(c => (
                <div key={c.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-bg-hover transition-colors">
                  <MessageSquare size={12} className="text-text-muted mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary truncate">{c.text}</p>
                    <p className="text-[10px] text-text-muted">{formatRelativeTime(c.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Waiting On */}
        <Section title="Waiting On" icon={<Hourglass size={14} className="text-warning" />}>
          {waitingByPerson.size === 0 ? (
            <EmptyState message="No tasks waiting on anyone." />
          ) : (
            <div className="space-y-3">
              {[...waitingByPerson.entries()].map(([personId, group]) => (
                <div key={personId}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-text-muted" />
                    <span className="text-xs font-semibold text-text-secondary">{group.name}</span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {group.tasks.map(t => <TaskRow key={t.id} task={t} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Follow Ups */}
        <Section title="Follow Ups" icon={<MessageSquare size={14} className="text-accent" />}>
          {followUpsByPerson.size === 0 ? (
            <EmptyState message="No follow-up tasks." />
          ) : (
            <div className="space-y-3">
              {[...followUpsByPerson.entries()].map(([personId, group]) => (
                <div key={personId}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-text-muted" />
                    <span className="text-xs font-semibold text-text-secondary">{group.name}</span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {group.tasks.map(t => <TaskRow key={t.id} task={t} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function TaskRow({ task, showDueDate }: { task: DashboardTask; showDueDate?: boolean }) {
  return (
    <Link
      to={`/tasks/${task.id}`}
      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-bg-hover transition-colors group"
    >
      <span className="flex-shrink-0">
        {STATUS_ICONS[task.status] ?? <Circle size={12} />}
      </span>
      <span className="text-sm text-text-primary group-hover:text-accent transition-colors truncate flex-1">
        {task.title}
      </span>
      {showDueDate && task.due_date && (
        <span className="text-[10px] text-text-muted flex-shrink-0">
          {formatShortDate(task.due_date)}
        </span>
      )}
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          PRIORITY_DOT_COLORS[task.priority] ?? 'bg-text-muted'
        }`}
        title={task.priority}
      />
    </Link>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  value: number;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-accent/10 text-accent">
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-text-primary">{value}</p>
          <p className="text-xs text-text-muted">{label}</p>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-text-secondary">{title}</h2>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-sm text-text-muted text-center py-4">{message}</p>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatShortDate(dateStr);
}
